import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
        },
        palette: {
          type: darkMode ? "dark" : "light",
          background: {
            lightBlue: '#edf8fd',
          },
          icons: {
            primary: "#00549D",
            secondary: "#F84E3B",
            gray: "#4f4f4f",
          },
          primary: {
            main: "#2396fb",
            dark: "#1c7ed6",
            light: "#81c784",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#F84E3B",
            contrastText: "#ffffff",
          },
          background: darkMode
            ? {
                default: "#1e1e1e",    // app background
                paper: "#2c2c2c",      // card/table/etc
              }
            : {
                default: "#f4f6f8",
                paper: "#ffffff",
              },
          text: darkMode
            ? {
                primary: "#ffffff",
                secondary: "#cfcfcf",
              }
            : {
                primary: "#1e1e1e",
                secondary: "#4f4f4f",
              },
        },
        overrides: {
          MuiListItemIcon: {
            root: {
              minWidth: 36,
            },
          },
          MuiButton: {
            containedPrimary: {
              backgroundColor: "#2396fb",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#1c7ed6",
              },
            },
            containedSecondary: {
              backgroundColor: "#F84E3B",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#cc3d2f",
              },
            },
          },
        },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
